/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDrag } from 'react-dnd';
import { ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';

import { useTargetMode } from '../../TargetModeContext';

function DragNode({ node, addToBuilder }) {
  const { activateTargetMode, canActivateTargetMode, registerSource } = useTargetMode();
  const sourceId = `side-panel:${node.uuid || node.id}`;
  const canChooseGroupingTarget = canActivateTargetMode({ kind: 'side-panel', node });
  const [isKeyboardTooltipOpen, setIsKeyboardTooltipOpen] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const addedTimerRef = useRef(null);

  const triggerAddedState = () => {
    setIsAdded(true);
    if (addedTimerRef.current) {
      clearTimeout(addedTimerRef.current);
    }
    addedTimerRef.current = setTimeout(() => {
      setIsAdded(false);
      addedTimerRef.current = null;
    }, 1000);
  };

  useEffect(
    () => () => {
      if (addedTimerRef.current) {
        clearTimeout(addedTimerRef.current);
      }
    },
    []
  );

  const [{ isDragging }, dragRef] = useDrag(
    {
      type: 'DragNode',
      item: { node, type: 'DragNode' },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (_item, monitor) => {
        if (monitor.didDrop()) {
          triggerAddedState();
        }
      },
    },
    [node]
  );

  const handleAddToBuilder = (n) => {
    addToBuilder(n);
    triggerAddedState();
  };

  return (
    <div className="query-builder-search-section__drag-node">
      <div className="query-builder-search-section__drag-node-content">
        <ButtonWithIcon
          type="link"
          id="layerIcon"
          linkIconSize="s"
          data-e2e="querybuilder-layers"
          sprite={node.canExist && node.children && node.children.length > 1 ? 'Layers' : 'Layer'}
          onClick={(e) => {
            handleAddToBuilder(node);
            e.stopPropagation();
          }}>
          {node.displayName}
        </ButtonWithIcon>
        <div aria-live="polite" aria-atomic="true">
          {isDragging && <span className="sr-only">{node.displayName} is being dragged to query builder</span>}
          {isAdded && <span className="sr-only">{node.displayName} added to query builder</span>}
        </div>
        <div ref={dragRef}>
          <Flyout
            trigger="hover"
            isControlled
            isOpen={isKeyboardTooltipOpen}
            onToggle={() => {}}
            shouldCloseOnESC
            flyout={canChooseGroupingTarget ? 'Drag or press Enter to choose a grouping target' : 'Drag or click to add to query builder'}
            placement="bottom"
            theme="simple">
            <ButtonWithIcon
              id={`querybuilder-sidepanel-dragfield-${node.uuid || node.id}`}
              ref={(element) => registerSource(sourceId, element)}
              draggable
              className="cursor-move"
              linkIconSize="xxs"
              data-e2e="querybuilder-dragfield"
              iconOnly
              type="borderless"
              aria-label={
                canChooseGroupingTarget ? `Choose grouping target for ${node.displayName}` : `Add ${node.displayName} to query builder`
              }
              sprite="Drag"
              onFocus={(event) => setIsKeyboardTooltipOpen(event.currentTarget.matches(':focus-visible'))}
              onBlur={() => setIsKeyboardTooltipOpen(false)}
              // Activation lives on click: a native button turns Enter/Space into a click, and
              // screen readers activate by emulating a click (possibly a real one, so detail
              // cannot be filtered on). An HTML5 drag never fires click, so drag is unaffected.
              // With nothing in the tree to group against, fall back to a plain add.
              onClick={() => {
                if (!canChooseGroupingTarget || !activateTargetMode({ kind: 'side-panel', node, sourceId })) {
                  handleAddToBuilder(node);
                }
              }}>
              drag
            </ButtonWithIcon>
          </Flyout>
        </div>
      </div>
    </div>
  );
}

DragNode.propTypes = {
  node: PropTypes.object,
  addToBuilder: PropTypes.func,
};

export default DragNode;
